import React, { useCallback, useContext, useEffect, useRef } from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import CrossIcon from '@atlaskit/icon/core/cross';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Inline, xcss } from '@atlaskit/primitives';
import {
	DynamicSurfaceView,
	useAISurfaceMetadata,
	useViewAIActions,
} from '@atlassian/ai-smart-button-api';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { useRovoButtonEventsInstrumentation } from '../../hooks/useRovoButtonEventsInstrumentation';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { AISmartButtonSidebarAPI } from '../common/types/shared-types';

import { AIDynamicSurfaceContext } from './AIDynamicSurfaceContext';
import { i18n as aiDynamicSurfaceI18n, RovoLogo } from './useDynamicSurfacePanel';
import { useIsElementVisible } from './useIsElementVisible';

const aiDynamicSurfaceHeaderContainerStyles = xcss({
	minHeight: '56px',
	paddingLeft: 'space.300',
	paddingRight: 'space.250',
	paddingTop: 'space.200',
	paddingBottom: 'space.150',
	width: '100%',
	position: 'sticky',
	backgroundColor: 'color.background.input',
	zIndex: 'navigation',
	top: 'space.0',
	borderRadius: 'inherit',
});

const aiDynamicSurfaceHeaderContainerGrayBackgroundStyles = xcss({
	backgroundColor: 'elevation.surface.sunken',
});

const panelNameAndIconContainerStyles = xcss({
	flex: 15,
	alignItems: 'center',
	justifyContent: 'flex-start',
});

const closePanelContainerStyles = xcss({
	flex: 1,
	alignItems: 'center',
	justifyContent: 'center',
});

const hiddenStyle = xcss({
	position: 'absolute',
	left: '-9999px',
	width: '1px',
	height: '1px',
	overflow: 'hidden',
});

export const AIDynamicSurfaceHeader = ({
	sidebarApi,
	isDefaultView,
	shouldHavePanelTitle,
}: {
	isDefaultView: boolean;
	shouldHavePanelTitle: boolean;
	sidebarApi: AISmartButtonSidebarAPI;
}) => {
	const { formatMessage } = useIntl();
	const hiddenRefForAccessibility = useRef<HTMLDivElement>(null);
	const headerRef = useRef<HTMLDivElement>(null);
	const surfaceMetadata = useAISurfaceMetadata();
	const { fireRovoButtonUIEvent } = useRovoButtonEventsInstrumentation();
	const headerIsVisible: boolean = useIsElementVisible(headerRef);
	const { activeAction } = useContext(AIDynamicSurfaceContext);
	const { changeView } = useViewAIActions();
	const setGrayBackground = !!shouldHavePanelTitle;

	// Focus the header when the panel is shown for accessibility
	useEffect(() => {
		if (hiddenRefForAccessibility.current && headerIsVisible) {
			hiddenRefForAccessibility.current?.focus();
		}
	}, [headerIsVisible]);

	const handleDynamicSurfaceClose = useCallback(() => {
		fireRovoButtonUIEvent({
			source: 'contentWrapper',
			action: 'clicked',
			actionSubject: 'rovoButton',
			actionSubjectId: 'buttonMenuDismissed',
			attributes: {
				product: surfaceMetadata?.product,
				productPage: surfaceMetadata?.contentType,
				pageMode: surfaceMetadata?.pageMode,
				menuItemName: activeAction?.title || 'default',
				menuItemType: activeAction?.type || 'default',
			},
			product: surfaceMetadata?.product || '',
		});

		void sidebarApi.closePanel();
	}, [fireRovoButtonUIEvent, sidebarApi, surfaceMetadata, activeAction]);

	const handleDynamicSurfaceBackButtonClick = useCallback(() => {
		changeView(DynamicSurfaceView.Default);
	}, [changeView]);

	const isInReferencingView = shouldHavePanelTitle;
	const isInDefaultView = !shouldHavePanelTitle && isDefaultView;
	const isInActionView = !isDefaultView && activeAction;

	return (
		<>
			<Flex
				xcss={[
					aiDynamicSurfaceHeaderContainerStyles,
					setGrayBackground && aiDynamicSurfaceHeaderContainerGrayBackgroundStyles,
				]}
				testId={'ai-dynamic-surface-header'}
				direction="row"
				role="banner"
				aria-label={
					shouldHavePanelTitle
						? formatMessage(aiDynamicSurfaceI18n.referencingPanelTitle)
						: undefined
				}
			>
				<Flex xcss={panelNameAndIconContainerStyles}>
					<Inline alignBlock="center" space="space.150">
						{
							<Box
								as="span"
								tabIndex={-1}
								xcss={hiddenStyle}
								ref={hiddenRefForAccessibility}
								aria-hidden="true"
							/>
						}

						{isInReferencingView && (
							<Heading size="small" as="h3" testId="ai-dynamic-surface-label" ref={headerRef}>
								{formatMessage(aiDynamicSurfaceI18n.referencingPanelTitle)}
							</Heading>
						)}
						{isInDefaultView && <RovoLogo />}
						{isInActionView && (
							<IconButton
								testId="ai-dynamic-surface-back-button"
								appearance="subtle"
								label={formatMessage(aiDynamicSurfaceI18n.backButtonLabel)}
								icon={ArrowLeftIcon}
								onClick={handleDynamicSurfaceBackButtonClick}
							/>
						)}
					</Inline>
				</Flex>
				<Flex xcss={closePanelContainerStyles}>
					<IconButton
						label={formatMessage(aiDynamicSurfaceI18n.closeButtonLabel)}
						onClick={handleDynamicSurfaceClose}
						appearance="subtle"
						icon={CrossIcon}
						testId="ai-dynamic-surface-close-button"
					/>
				</Flex>
			</Flex>
		</>
	);
};
